import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { message, Checkbox } from 'antd';
import { CloseOutlined } from '@ant-design/icons'
import { Form } from 'antd';
import LoginForm, { LoginValues } from './LoginForm';
import Description from './Description';
import TermsAgreement from './TermsAgreement';
import './styles.less';

interface SharedLoginProps {
  visible: boolean;
  onClose: () => void;
  logo: string;
  loginApi: (values: Record<string, any>) => Promise<any>;
  sendSmsCodeApi: (phone: string, type: string) => Promise<any>;
  onSuccess:  (user: any) => void;
}

const SharedLogin: React.FC<SharedLoginProps> = ({
    visible, onClose, logo, loginApi, sendSmsCodeApi, onSuccess
  }) => {
  const [loginForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(true); // 默认勾选
  const [loginError, setLoginError] = useState(''); // 添加错误状态

  // 通过CSS类控制页面滚动
  useEffect(() => {
    if (visible) {
      document.body.classList.add('no-scroll');
      // 设置top值来保持当前滚动位置
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [visible]);

  const handleLogin = async (values: LoginValues) => {
    // 清除之前的错误信息
    setLoginError('');
    
    // 检查是否同意协议
    if (!agreedToTerms) {
      setLoginError('请先同意个人隐私协议和账号使用规则');
      return;
    }

    setLoading(true);
    try {
      const response = await loginApi({
        userName: values.username,
        password: values.password,
        type: values.type,
        phoneNumber: values.phone,
        smsCode: values.smsCode
      });
      
      if (!response.code) {
        message.success('登录成功');
        onClose();
        onSuccess(response.data);
      } else {
        // 将错误信息显示在表单下方而不是弹窗
        setLoginError('登录失败：' + response.message);
      }
    } catch (error) {
      console.error('登录错误:', error);
      // 将错误信息显示在表单下方而不是弹窗
      setLoginError('登录过程中发生错误，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsChange = (e: any) => {
    setAgreedToTerms(e.target.checked);
    // 清除错误信息当用户勾选协议时
    if (e.target.checked && loginError === '请先同意个人隐私协议和账号使用规则') {
      setLoginError('');
    }
  };

  if (!visible) return null;

  const loginContent = (
    <div className="login-overlay">
      <div className="login-container">
        {/* 左侧文案区域 */}
        <Description logo={logo} />

        {/* 右侧登录表单区域 */}
        <div className="login-right">
          <div className="login-form-container">
            <div className="form-header">
              <h2>欢迎使用橙子8</h2>
              <p>请登录您的账户使用</p>
            </div>
            
            <LoginForm
              form={loginForm}
              loading={loading}
              onFinish={handleLogin}
              sendSmsCodeApi={sendSmsCodeApi}
              disabled={!agreedToTerms}
            />
            
            {/* 错误信息显示区域 */}
            {loginError && (
              <div className="login-error">
                <span className="error-text">{loginError}</span>
              </div>
            )}
            
            <TermsAgreement 
              checked={agreedToTerms}
              onChange={handleTermsChange}
            />
          </div>
          
          <button className="close-button" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>
      </div>
    </div>
  );

  // 使用createPortal将组件渲染到body
  return createPortal(loginContent, document.body);
};

export default SharedLogin;
